import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react'

const HOLD_DELAY = 450
const HOLD_FAST_AT = 1500
const INTERVAL_SLOW = 120
const INTERVAL_FAST = 40

type UseHoldStepperOptions = {
  value: number
  min: number
  max: number
  step?: number
  onChange: (value: number) => void
}

export function useHoldStepper({ value, min, max, step = 1, onChange }: UseHoldStepperOptions) {
  const [activeZone, setActiveZone] = useState<'dec' | 'inc' | null>(null)
  const [atLimit, setAtLimit] = useState<'min' | 'max' | null>(null)

  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef(0)
  const valueRef = useRef(value)
  valueRef.current = value

  const snap = useCallback(
    (v: number) => parseFloat((Math.round(v / step) * step).toFixed(10)),
    [step],
  )

  const apply = useCallback(
    (dir: 1 | -1) => {
      const candidate = snap(valueRef.current + dir * step)
      const next = Math.min(max, Math.max(min, candidate))
      if (next === valueRef.current) {
        const limit = dir > 0 ? 'max' : 'min'
        setAtLimit(limit)
        setTimeout(() => setAtLimit((cur) => (cur === limit ? null : cur)), 350)
      } else {
        onChange(next)
      }
    },
    [min, max, step, snap, onChange],
  )

  const stopHold = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current)
      holdTimerRef.current = null
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setActiveZone(null)
  }, [])

  const startHold = useCallback(
    (dir: 1 | -1) => {
      apply(dir)
      startTimeRef.current = Date.now()
      setActiveZone(dir > 0 ? 'inc' : 'dec')

      holdTimerRef.current = setTimeout(() => {
        const slow = setInterval(() => {
          const elapsed = Date.now() - startTimeRef.current
          if (elapsed > HOLD_FAST_AT) {
            clearInterval(slow)
            intervalRef.current = setInterval(() => apply(dir), INTERVAL_FAST)
          } else {
            apply(dir)
          }
        }, INTERVAL_SLOW)
        intervalRef.current = slow
      }, HOLD_DELAY)
    },
    [apply],
  )

  useEffect(() => stopHold, [stopHold])

  const bindDecrement = useCallback(
    () => ({
      onPointerDown: (e: PointerEvent) => {
        e.preventDefault()
        e.currentTarget.setPointerCapture(e.pointerId)
        startHold(-1)
      },
      onPointerUp: stopHold,
      onPointerLeave: stopHold,
      onPointerCancel: stopHold,
    }),
    [startHold, stopHold],
  )

  const bindIncrement = useCallback(
    () => ({
      onPointerDown: (e: PointerEvent) => {
        e.preventDefault()
        e.currentTarget.setPointerCapture(e.pointerId)
        startHold(1)
      },
      onPointerUp: stopHold,
      onPointerLeave: stopHold,
      onPointerCancel: stopHold,
    }),
    [startHold, stopHold],
  )

  const bindDualZone = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      const rect = e.currentTarget.getBoundingClientRect()
      const dir: 1 | -1 = e.clientX - rect.left >= rect.width / 2 ? 1 : -1
      e.currentTarget.setPointerCapture(e.pointerId)
      setActiveZone(dir > 0 ? 'inc' : 'dec')
      startHold(dir)
    },
    [startHold],
  )

  const pct = max > min ? Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100)) : 0

  return {
    activeZone,
    atLimit,
    stopHold,
    bindDecrement,
    bindIncrement,
    bindDualZone,
    pct,
  }
}
